import { UIConstants, isMercuryTenant } from '../../shared/models/Constants';
import { LifecycleGridItem } from './MyAccessRequests.types';

const normalizeStatus = (value?: string): string => {
  const normalized = (value || '').toLowerCase().replace(/\s+/g, '');
  return normalized === 'complete' ? 'completed' : normalized;
};
const normalizeRequestType = (value?: string): string => (value || '').toLowerCase().replace(/\s+/g, '');

export interface EligibilityResult {
  isEligible: boolean;
  reason?: string;
}

export interface LifecycleEligibilityContext {
  tenantName?: string;
  allItems: LifecycleGridItem[];
  accessExpiryReminderWindowDays: number;
  approvedAccesses?: ApprovedAccessLite[];
}

export type ApprovedAccessLite = {
  roleDefinitionId?: string;
  endDate?: string;
  allowRemove?: boolean;
};

export interface RemoveEligibilityContext {
  allItems: LifecycleGridItem[];
  approvedAccesses: ApprovedAccessLite[];
}

const unsupportedRequestTypes = new Set([
  normalizeRequestType(UIConstants.RequestType.Remove),
  normalizeRequestType(UIConstants.RequestType.BulkRemove),
  normalizeRequestType(UIConstants.RequestType.AttestationExpired),
  normalizeRequestType('deprovision'),
  normalizeRequestType('auto expired'),
]);

const isInProgressStatus = (value?: string): boolean => normalizeStatus(value) === 'inprogress';

const isSameLifecycleRole = (left: LifecycleGridItem, right: LifecycleGridItem, tenantName?: string): boolean => {
  if (!left || !right) {
    return false;
  }

  if (left.roleId && right.roleId) {
    return left.roleId === right.roleId;
  }

  return (left.role || '').toLowerCase() === (right.role || '').toLowerCase();
};

const hasPendingForSameRole = (item: LifecycleGridItem, context: LifecycleEligibilityContext): boolean => {
  return context.allItems.some((candidate) =>
    candidate.requestId !== item.requestId
    && isInProgressStatus(candidate.status)
    && isSameLifecycleRole(candidate, item, context.tenantName)
  );
};

const hasMatchingApprovedAccessForLifecycle = (item: LifecycleGridItem, approvedAccesses: ApprovedAccessLite[] = []): boolean => {
  if (!item?.roleId) {
    return true;
  }

  if (!approvedAccesses.length) {
    return false;
  }

  const itemEnd = item?.originalEndDate ? new Date(item.originalEndDate).getTime() : NaN;
  return approvedAccesses.some((access) => {
    if (access?.roleDefinitionId !== item.roleId) {
      return false;
    }

    const accessEnd = access?.endDate ? new Date(access.endDate).getTime() : NaN;
    if (Number.isNaN(itemEnd) || Number.isNaN(accessEnd)) {
      return true;
    }

    return Math.abs(itemEnd - accessEnd) <= 60000;
  });
};

const evaluateRenewWindow = (item: LifecycleGridItem, context: LifecycleEligibilityContext): EligibilityResult => {
  if (!item.roleAllowRenew) {
    return { isEligible: false, reason: 'Renew is not allowed for this role' };
  }

  if (!item.originalEndDate) {
    return { isEligible: false, reason: 'Missing end date for renew eligibility' };
  }

  const endDate = new Date(item.originalEndDate).getTime();
  if (Number.isNaN(endDate)) {
    return { isEligible: false, reason: 'Invalid end date for renew eligibility' };
  }

  const diffInDays = Math.floor((endDate - new Date().getTime()) / (24 * 60 * 60 * 1000));
  if (diffInDays > context.accessExpiryReminderWindowDays) {
    return { isEligible: false, reason: `Renew is available within ${context.accessExpiryReminderWindowDays} days of end date` };
  }

  return { isEligible: true };
};

export const isEditRenewSupportedTenant = (tenantName?: string): boolean => {
  if (!tenantName) {
    return false;
  }

  return (
    isMercuryTenant(tenantName)
    || tenantName === UIConstants.Tenant.Alfred
    || tenantName === UIConstants.Tenant.MDS
    || tenantName === UIConstants.Tenant.MSSales
  );
};

const evaluateCommonLifecycleEligibility = (item: LifecycleGridItem, context: LifecycleEligibilityContext): EligibilityResult => {
  const status = normalizeStatus(item.status);
  if (status !== 'approved' && status !== 'completed') {
    return { isEligible: false, reason: 'Only approved/completed access can be modified' };
  }

  if (!item.roleId) {
    return { isEligible: false, reason: 'Missing roleId' };
  }

  const requestType = normalizeRequestType(item.requestType);
  if (unsupportedRequestTypes.has(requestType)) {
    return { isEligible: false, reason: 'This request type is not eligible for this action' };
  }

  if (!hasMatchingApprovedAccessForLifecycle(item, context.approvedAccesses || [])) {
    return { isEligible: false, reason: 'No active approved access found for this role/request' };
  }

  if (hasPendingForSameRole(item, context)) {
    return { isEligible: false, reason: 'There is a pending request for this role' };
  }

  return { isEligible: true };
};

export const evaluateEditEligibility = (item: LifecycleGridItem, context: LifecycleEligibilityContext): EligibilityResult => {
  const common = evaluateCommonLifecycleEligibility(item, context);
  if (!common.isEligible) {
    return common;
  }

  if (item.allowEdit === false) {
    return { isEligible: false, reason: 'Edit is not allowed for this role' };
  }

  return { isEligible: true };
};

export const evaluateRenewEligibility = (item: LifecycleGridItem, context: LifecycleEligibilityContext): EligibilityResult => {
  const common = evaluateCommonLifecycleEligibility(item, context);
  if (!common.isEligible) {
    return common;
  }

  return evaluateRenewWindow(item, context);
};

export const hasMatchingApprovedAccess = (item: LifecycleGridItem, approvedAccesses: ApprovedAccessLite[]): boolean => {
  if (!item?.roleId) {
    return true;
  }

  if (approvedAccesses.length === 0) {
    return false;
  }

  // Legacy parity: remove eligibility is role-based (plus tenant-specific flags), not endDate-based.
  // Mercury-specific restrictions are carried in access.allowRemove and enforced here.
  return approvedAccesses.some((access) => {
    if (access?.roleDefinitionId !== item.roleId) {
      return false;
    }

    if (access.allowRemove === false) {
      return false;
    }

    return true;
  });
};

export const hasExistingRemoveForSameRole = (item: LifecycleGridItem, allItems: LifecycleGridItem[]): boolean => {
  if (!item?.roleId || !allItems?.length) {
    return false;
  }

  return allItems.some((candidate) => {
    if (candidate?.requestId === item.requestId || candidate?.roleId !== item.roleId) {
      return false;
    }

    const requestType = normalizeRequestType(candidate?.requestType);
    const status = normalizeStatus(candidate?.status);
    const isRemoveType = requestType === normalizeRequestType(UIConstants.RequestType.Remove);
    const isBlockingStatus =
      status.startsWith('inprogress')
      || status.startsWith('approved')
      || status.startsWith('completed')
      || status.startsWith('complete');

    if (!isRemoveType || !isBlockingStatus) {
      return false;
    }

    // The remove row must have been requested AFTER this item. RequestIds encode ticks
    // (EUA-REQ-{18-digit-ticks}), so lexicographic comparison gives chronological order.
    return (candidate.requestId ?? '') > (item.requestId ?? '');
  });
};

const removeLikeRequestTypes = new Set([
  normalizeRequestType(UIConstants.RequestType.Remove),
  normalizeRequestType(UIConstants.RequestType.BulkRemove),
  normalizeRequestType(UIConstants.RequestType.AttestationExpired),
  normalizeRequestType('deprovision'),
  normalizeRequestType('auto expired'),
]);

export const hasNewerApprovedGrantForSameRole = (item: LifecycleGridItem, allItems: LifecycleGridItem[]): boolean => {
  if (!item?.roleId || !allItems?.length) {
    return false;
  }

  return allItems.some((candidate) => {
    if (candidate?.requestId === item.requestId || candidate?.roleId !== item.roleId) {
      return false;
    }

    if ((candidate.requestId ?? '') <= (item.requestId ?? '')) {
      return false;
    }

    const requestType = normalizeRequestType(candidate?.requestType);
    if (removeLikeRequestTypes.has(requestType)) {
      return false;
    }

    const status = normalizeStatus(candidate?.status);
    return status.startsWith('approved') || status.startsWith('completed') || status.startsWith('complete');
  });
};

export const evaluateCancelEligibility = (item: LifecycleGridItem): EligibilityResult => {
  return normalizeStatus(item?.status) === 'inprogress'
    ? { isEligible: true }
    : { isEligible: false, reason: 'Only InProgress requests can be canceled' };
};

export const evaluateRemoveEligibility = (item: LifecycleGridItem, context: RemoveEligibilityContext): EligibilityResult => {
  const status = normalizeStatus(item?.status);
  const canRemoveByStatus = status.startsWith('approved') || status.startsWith('completed') || status.startsWith('complete');
  if (!canRemoveByStatus) {
    return { isEligible: false, reason: 'Only approved/completed access can be removed' };
  }

  const requestType = normalizeRequestType(item?.requestType);
  if (requestType === normalizeRequestType(UIConstants.RequestType.Remove)) {
    return { isEligible: false, reason: 'Item is already a remove request' };
  }

  if (hasNewerApprovedGrantForSameRole(item, context.allItems || [])) {
    return { isEligible: false, reason: 'A newer approved grant exists for this role' };
  }

  if (hasExistingRemoveForSameRole(item, context.allItems || [])) {
    return { isEligible: false, reason: 'There is already a remove request for this role' };
  }

  if (!hasMatchingApprovedAccess(item, context.approvedAccesses || [])) {
    return { isEligible: false, reason: 'No active approved access found for this role/request' };
  }

  if (!item?.roleId) {
    return { isEligible: false, reason: 'Missing roleId' };
  }

  return { isEligible: true };
};
